import { CalendarDays, Download } from "lucide-react";
import type { ReactNode } from "react";
import { useLocation } from "react-router";

import { Button } from "@/components/ui/Button";

import { Breadcrumbs, type Crumb } from "./Breadcrumbs";
import { levelForPath, type LevelContext } from "@/lib/portfolioLevels";
import { REPORTING_YEAR } from "@/lib/reportingYear";

import { PortfolioLevelBar } from "./LevelBar";
import "./PageHeader.css";

type PageHeaderProps = {
  title: string;
  /** Eén regel die zegt waar het scherm over gaat. */
  description?: string;
  breadcrumbs?: Crumb[];
  /**
   * Peildatum, al opgemaakt aangeleverd. De frontend maakt ook een datum niet
   * zelf op: welke peildatum bij een cijfer hoort, weet de rekenkern.
   */
  referenceDate?: { label: string; display: string; reportingYear?: string };
  /** Extra knoppen links van Exporteren, bijvoorbeeld een filter. */
  actions?: ReactNode;
  onExport?: () => void;
  /** Zet de exportknop uit met een uitleg, bijvoorbeeld bij een lege pagina. */
  exportDisabledReason?: string;
  /** Geen exportknop, op een scherm waar exporteren geen betekenis heeft (beheer). */
  hideExport?: boolean;
  /** Links van de titel, bijvoorbeeld de straatbeeldfoto van een object. */
  media?: ReactNode;
  /** Direct naast de titel: een statusmerkje. */
  badge?: ReactNode;
  /** Onder de omschrijving: de kernfeiten van een detailscherm. */
  meta?: ReactNode;
  /**
   * Rechts naast de omschrijving, onder de titel (ronde 4, mockup 39): een
   * schakelaar die bij de inhoud hoort en niet bij de knoppen. De titel houdt
   * zo de hele breedte.
   */
  aside?: ReactNode;
  /**
   * Het complex en pand van een detailscherm, voor de niveaubalk (FB-110): dan
   * gaat Complex naar dít complex en Pand naar dít pand. Een lijst geeft niets
   * mee; daar komt de context uit de querystring.
   */
  levels?: LevelContext;
};

/**
 * De paginakop die op elk scherm hetzelfde is: kruimelpad, titel, peildatum en
 * exporteren. In de prototypeschermen heeft vrijwel elke pagina die drie,
 * daarom staat het hier één keer en niet per scherm opnieuw.
 */
export function PageHeader({
  title,
  description,
  breadcrumbs = [],
  referenceDate,
  actions,
  onExport,
  exportDisabledReason,
  hideExport = false,
  media,
  badge,
  meta,
  aside,
  levels,
}: PageHeaderProps) {
  const { pathname, hash } = useLocation();
  // FB-110: op elk scherm van Vastgoed, ook een pand- of objectscherm.
  const toonNiveaus = levelForPath(pathname, hash) !== null;
  // Stijl A (ontwerp 28-10-2026): het verslagjaar staat al als chip in de kop
  // van de applicatie; hier herhalen voegt niets toe. Een andere peildatum
  // (bijvoorbeeld een landelijk gemiddelde) blijft wel staan.
  const toonPeildatum =
    referenceDate &&
    !(referenceDate.label === "Verslagjaar" && referenceDate.display === String(REPORTING_YEAR) && !referenceDate.reportingYear);
  // Een exportknop die alleen uit kan staan (geen handeling, wel een reden) is
  // in het ontwerp geen knop: hij verschijnt zodra exporteren echt kan.
  const toonExport = !hideExport && !(exportDisabledReason && !onExport);
  const acties = (
    <div className="atl-pageheader__actions">
      {/* Vastgoed: de niveaubalk staat rechts in de kop (ontwerp 28-10-2026, FB-110). */}
      {toonNiveaus && <PortfolioLevelBar context={levels} />}
      {toonPeildatum && referenceDate && (
        <p className="atl-pageheader__reference">
          <CalendarDays size={15} strokeWidth={1.75} aria-hidden />
          <span>
            <span className="atl-pageheader__reference-label">{referenceDate.label}</span>{" "}
            <span className="atl-numeric">{referenceDate.display}</span>
            {referenceDate.reportingYear && (
              <span className="atl-pageheader__reference-year"> · {referenceDate.reportingYear}</span>
            )}
          </span>
        </p>
      )}
      {actions}
      {toonExport && (
        <Button
          variant="secondary"
          icon={<Download size={15} strokeWidth={2} aria-hidden />}
          onClick={onExport}
          disabled={Boolean(exportDisabledReason)}
          title={exportDisabledReason}
        >
          Exporteren
        </Button>
      )}
    </div>
  );

  // Met een foto (detailscherm) staan de knoppen op de regel van het
  // kruimelpad, zodat titel en kernfeiten de volle breedte houden; anders
  // brak een adres als titel over twee regels en viel de laatste kernfeit
  // eronder (Tom, 23-09-2026).
  const bovenaan = Boolean(media);

  return (
    <div className="atl-pageheader">
      {(breadcrumbs.length > 0 || bovenaan) && (
        <div className="atl-pageheader__top">
          {breadcrumbs.length > 0 && <Breadcrumbs items={breadcrumbs} />}
          {bovenaan && acties}
        </div>
      )}

      <div className="atl-pageheader__row">
        {media && <div className="atl-pageheader__media">{media}</div>}
        <div className="atl-pageheader__titles">
          <div className="atl-pageheader__titleline">
            <h1 className="atl-pageheader__title">{title}</h1>
            {badge}
          </div>
          {aside ? (
            <div className="atl-pageheader__descline">
              {description && <p className="atl-pageheader__description">{description}</p>}
              <div className="atl-pageheader__aside">{aside}</div>
            </div>
          ) : (
            description && <p className="atl-pageheader__description">{description}</p>
          )}
          {meta && <div className="atl-pageheader__meta">{meta}</div>}
        </div>

        {!bovenaan && acties}
      </div>
    </div>
  );
}
